import { DraftLanguage, SOCIAL_NETWORK_ORDER, SocialNetwork } from 'models/socialCampaign'
import { FC, useId } from 'react'
import { GRAY_700, GRAY_800 } from 'constants/styling/theme'

import { MUIButton } from 'components/common/MUIButton'
import { MUIRadioCard } from 'components/common/MUICards'
import { NetworkMark } from './NetworkMark.component'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import { SystemMessage } from 'components/common/SystemMessage'
import Typography from '@mui/material/Typography'
import styles from './CampaignSettingsPanel.module.sass'
import { useTranslation } from 'react-i18next'

const LANGUAGES = [DraftLanguage.EN, DraftLanguage.DE]

interface Props {
  open: boolean
  onClose: () => void
  /** A draft re-plans on a new language; a launched campaign applies it to the posts generated next */
  draft: boolean
  networks: SocialNetwork[]
  language: DraftLanguage
  onNetworkChange: (network: SocialNetwork, selected: boolean) => void
  onLanguageChange: (language: DraftLanguage) => void
  /** The last change could not be saved */
  failed: boolean
  /** Approve all or Launch is on its way: a change now would re-plan or re-target the posts being launched */
  disabled?: boolean
}

/**
 * Networks and language (prototype v3 `renderStrategy`, v2 decision 17): a side panel, a sheet on a
 * phone, with radio cards (the networks' independent toggles, each as wide as its content). Changes
 * apply as they are made; Done closes. The backend needs one network at least, so the last one ticked
 * cannot be unticked.
 */
export const CampaignSettingsPanel: FC<Props> = ({ open, onClose, draft, networks, language, onNetworkChange, onLanguageChange, failed, disabled = false }) => {
  const { t } = useTranslation(['social_campaign'])
  const id = useId().replace(/:/g, '')
  const lastNetwork = networks.length === 1

  return (
    <ResponsiveDrawer
      isOpen={open}
      onClose={onClose}
      isAccessibleDialog
      anchor="right"
      width="48rem"
      backdrop="dark"
      title={t('settings.title')}
      subTitleSlot={<Typography variant="text-sm" color={GRAY_700}>{draft ? t('settings.description_draft') : t('settings.description_live')}</Typography>}
      actionsSlot={<MUIButton onClick={onClose}>{t('settings.done')}</MUIButton>}
    >
      <div className={styles.panel}>

        {failed && <SystemMessage variant="error" role="alert" message={t('flash.error')} isRawContent />}

        <div className={styles.field}>

          <Typography id={`${id}-networks`} variant="text-sm" fontWeight={450} color={GRAY_800}>{t('settings.networks')}</Typography>

          <div className={styles.cards} role="group" aria-labelledby={`${id}-networks`}>
            {SOCIAL_NETWORK_ORDER.map((network) => {
              const selected = networks.includes(network)
              const locked = disabled || (selected && lastNetwork)
              return (
                <MUIRadioCard
                  key={network}
                  name={`${id}-networks`}
                  value={network}
                  isMultiple
                  width="hug"
                  checked={selected}
                  disabled={locked}
                  aria-describedby={selected && lastNetwork ? `${id}-last-network` : undefined}
                  icon={<NetworkMark network={network} size={16} />}
                  label={t(`network.${network}`)}
                  // a disabled input can still be toggled by a scripted click: the panel holds its value itself
                  onChange={() => !locked && onNetworkChange(network, !selected)}
                />
              )
            })}
          </div>

          {lastNetwork && <Typography id={`${id}-last-network`} variant="text-xs" component="p" color={GRAY_700}>{t('settings.last_network')}</Typography>}

        </div>

        <div className={styles.field}>

          <Typography id={`${id}-language`} variant="text-sm" fontWeight={450} color={GRAY_800}>{t('settings.language')}</Typography>

          <div className={styles.cards} role="radiogroup" aria-labelledby={`${id}-language`}>
            {LANGUAGES.map((option) => (
              <MUIRadioCard
                key={option}
                name={`${id}-language`}
                value={option}
                width="hug"
                checked={language === option}
                disabled={disabled}
                label={t(`language.${option}`)}
                onChange={() => !disabled && onLanguageChange(option)}
              />
            ))}
          </div>

        </div>

      </div>
    </ResponsiveDrawer>
  )
}
